import React, { useEffect, useState } from 'react';
import { Box, Typography, Tabs, Tab, Alert, Button, Paper } from '@mui/material';
import { useParams } from 'react-router-dom';
import WhatsAppSettings from '../components/WhatsApp/WhatsAppSettings';
import WhatsAppTemplates from '../components/WhatsApp/WhatsAppTemplates';
import WhatsAppBroadcast from '../components/WhatsApp/WhatsAppBroadcast';
import { useSelector } from 'react-redux';
import axios from 'axios';
import storage from 'src/utils/storageUtils';

function WhatsAppContainer() {
  const { clinicId: routeClinicId } = useParams();
  const clinicState = useSelector((state) => state.app.clinics);
  const currentClinic = clinicState?.currentClinic;
  const clinicId = currentClinic?.id || clinicState?.clinicId || routeClinicId || storage.get('CLINIC_ID');

  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (clinicId) {
      fetchStatus();
    } else {
      setLoading(false);
    }
  }, [clinicId]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const token = storage.get('TOKEN');
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/whatsapp/status/${clinicId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setStatusData(response.data);
    } catch (error) {
      console.error("Failed to fetch WhatsApp status", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectSuccess = (data) => {
    setStatusData(data);
    fetchStatus();
  };

  const handleDisconnect = async () => {
    try {
      setLoading(true);
      const token = storage.get('TOKEN');
      await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/whatsapp/disconnect/${clinicId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setStatusData({ status: 'DISCONNECTED' });
      setTabIndex(0);
    } catch (error) {
      console.error("Failed to disconnect", error);
    } finally {
      setLoading(false);
    }
  };

  const isConnected = statusData?.status === 'CONNECTED';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', overflow: 'hidden' }}>
      
      {/* Top Header / Navigation Tabs */}
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 2, md: 4 }, pt: 2, bgcolor: '#fff' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box>
            <Typography variant="h3" fontWeight="bold">WhatsApp Marketing & Automation</Typography>
            <Typography variant="body2" color="textSecondary">Manage Meta WhatsApp templates, campaigns, and Embedded Signup</Typography>
          </Box>
          <Box>
            <Typography 
              variant="caption" 
              sx={{ 
                px: 1.5, 
                py: 0.5, 
                borderRadius: 2, 
                fontWeight: 'bold', 
                bgcolor: isConnected ? '#e8f5e9' : '#fff3e0', 
                color: isConnected ? '#2e7d32' : '#e65100',
                border: `1px solid ${isConnected ? '#a5d6a7' : '#ffcc80'}`
              }}
            >
              {isConnected ? `CONNECTED: ${statusData?.displayPhoneNumber || ''}` : 'META WABA: NOT LINKED'}
            </Typography>
          </Box>
        </Box>

        <Tabs 
          value={tabIndex} 
          onChange={(e, newValue) => setTabIndex(newValue)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="Template Manager" sx={{ fontWeight: '600' }} />
          <Tab label="Broadcast & Campaigns" sx={{ fontWeight: '600' }} />
          <Tab label="Settings & Connect" sx={{ fontWeight: '600' }} />
        </Tabs>
      </Paper>

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, overflowY: 'auto' }}>
        {!isConnected && !loading && tabIndex !== 2 && (
          <Alert 
            severity="info" 
            action={
              <Button color="primary" variant="outlined" size="small" onClick={() => setTabIndex(2)}>
                Connect WABA
              </Button>
            }
            sx={{ mb: 3, borderRadius: 2 }}
          >
            WhatsApp Business API is not linked for this clinic yet. You can manage templates locally, or connect your account in the <strong>Settings & Connect</strong> tab to sync directly with Meta.
          </Alert>
        )}

        {tabIndex === 0 && <WhatsAppTemplates clinicId={clinicId} isConnected={isConnected} onGoToSettings={() => setTabIndex(2)} />}
        {tabIndex === 1 && <WhatsAppBroadcast clinicId={clinicId} isConnected={isConnected} onGoToSettings={() => setTabIndex(2)} />}
        {tabIndex === 2 && (
          <WhatsAppSettings 
            statusData={statusData} 
            loading={loading}
            clinicId={clinicId}
            onConnectSuccess={handleConnectSuccess}
            onDisconnect={handleDisconnect}
          />
        )}
      </Box>

    </Box>
  );
}

export default WhatsAppContainer;
